import {Injectable, Injector} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, CanLoad, Route, Router, RouterStateSnapshot} from '@angular/router';
import {AuthenticationService, CookieService} from '@synisys/idm-authentication-client-js';
import {Observable} from 'rxjs/Observable';
import {PlatformLocation} from '@angular/common';
import {RedirectUrlHelper} from "@synisys/idm-ui-shell/app/services/redirect-url-helper";
import {map, shareReplay, switchMap} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {ApplicationPropertiesService} from "@synisys/idm-application-properties-service-client-js";
import {fromPromise} from "rxjs/observable/fromPromise";

/**
 * Guard to check if requested user is logged
 */
@Injectable()
export class CustomLoginGuard implements CanActivate, CanLoad {

	private httpClient: HttpClient;
	public applicationProperties: ApplicationPropertiesService;
	private agraServiceUrl$: Observable<string>;
	private authServiceUrl$: Observable<string>;

	constructor(
		private authenticationService: AuthenticationService,
		private router: Router,
		private cookieService: CookieService,
		private platformLocation: PlatformLocation,
		private injector: Injector
	) {}

	/**
	 * Check if user logged then return true otherwise navigate on login page
	 * @returns true if user is logged otherwise false
	 */
	canActivate(routeSnapshot: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		this.authenticationService.redirectUrl = state.url;
		return this.authenticationService
			.isLoggedOn()
			.map(isLogged => {
				if (!isLogged) {
					this.router.navigate(['/login']);
					return false;
				} else if (this.cookieService.get('csrfToken')) {
					// This is for sso users
					localStorage.setItem('csrfToken', this.cookieService.get('csrfToken'));
					this.cookieService.delete('csrfToken');
				}
				if (state.url === '' || state.url === '/') {
					this.isUserFromAgraOrg().first().subscribe(isAgra => {
						if (isAgra) {
							this.router.navigate(['/pages/48']);
						} else {
							this.router.navigate(['/portfolio/requests']);
						}
					});
				}
				return isLogged;
			})
			.take(1);
	}

	canLoad(route: Route): Observable<boolean> | Promise<boolean> | boolean {
		this.authenticationService.redirectUrl = RedirectUrlHelper.getUrlAfterAppIdPart(this.platformLocation);
		return this.authenticationService
			.isLoggedOn()
			.map(isLogged => {
				if (!isLogged) {
					this.router.navigate(['/login']);
					return false;
				}
				return isLogged;
			})
			.take(1);
	}

	private isUserFromAgraOrg(): Observable<boolean> {
		this.injectDependencies();
		return this.authServiceUrl$.pipe(
			switchMap((url) => this.httpClient.get<{ userId: { userData: any } }>(`${url}/me`)),
			map((response) => response.userId),
			switchMap((userId) =>
				this.agraServiceUrl$.pipe(
					switchMap((url) => this.httpClient.get<{ data: any }>(`${url}/common/user/organization/${userId}`)),
					map((orgId) => orgId)
				)
			),
			switchMap(this.checkOrganization.bind(this))
		);
	}

	private checkOrganization(organizationId: number): Observable<boolean> {
		return Observable.of(organizationId === 1);
	}

	private injectDependencies(): void {
		this.httpClient = this.injector.get(HttpClient);
		this.applicationProperties = this.injector.get(ApplicationPropertiesService);
		this.agraServiceUrl$ = fromPromise(this.applicationProperties.getProperty('agra-service-url')).pipe(shareReplay());
		this.authServiceUrl$ = fromPromise(this.applicationProperties.getProperty('auth-service-url')).pipe(shareReplay());
	}
}
